import { Box, Divider, HStack, Stack, Text } from '@chakra-ui/react';
import { Accordion, Checkbox } from '@morressier/alembic';
import { IReviewerRecommendation } from '@publishing-engine/domain/models';
import { IReviewerCOI } from '@publishing-engine/domain/views';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faUniversity } from '@fortawesome/pro-regular-svg-icons';
import { validatorForAddReviewer } from 'src/api/queries';
import { useReviewerInviteList } from 'src/contexts/ReviewerInviteListProvider';
import ReviewerConflictInfo from './ReviewerConflictInfo';

interface RecommendedReviewersProps {
  reviewers: IReviewerRecommendation[];
  title: string;
  submissionContextId: string;
  submissionId: string;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

interface ReviewerItemProps {
  reviewer: IReviewerRecommendation;
  index: number;
  isSelected: boolean;
  isInvited: boolean;
  coi?: IReviewerCOI;
  onChange: (reviewer: IReviewerRecommendation) => void;
}

const ReviewerItem = ({
  reviewer,
  index,
  isSelected,
  isInvited,
  coi,
  onChange
}: ReviewerItemProps) => {
  const { email, affiliation, reason } = reviewer;

  const displayName =
    [reviewer?.firstName, reviewer?.lastName].filter(Boolean).join(' ') || '--';

  return (
    <Stack key={email ?? index} mt={2} mb={0} p={1} spacing={1.5}>
      <HStack align="start">
        <Checkbox
          mb={1.5}
          ariaLabel="select-reviewer"
          id={email ?? `reviewer-${index}`}
          name="reviewer-info"
          isChecked={isSelected}
          disabled={isInvited}
          onChange={() => onChange(reviewer)}
        >
          <Text textStyle="textBody" fontWeight="600">
            {displayName}
          </Text>
        </Checkbox>
      </HStack>

      {affiliation && (
        <HStack align="start">
          <Box mt={-0.5}>
            <FontAwesomeIcon icon={faUniversity} />
          </Box>

          <Text textStyle="textContext">{affiliation}</Text>
        </HStack>
      )}

      {email && (
        <HStack align="start">
          <Box mt={-0.3}>
            <FontAwesomeIcon icon={faEnvelope} />
          </Box>

          <Text textStyle="textContext">{email}</Text>
        </HStack>
      )}

      {reason && (
        <Box>
          <Text textStyle="textContext" fontWeight="600" mb={1}>
            Reason for Recommendation
          </Text>

          <Text fontWeight="500" textStyle="headingOverline">
            {reason}
          </Text>
        </Box>
      )}

      {email && coi && (
        <Box mt={1}>
          <ReviewerConflictInfo
            errors={coi.error}
            warnings={coi.warning}
            info={coi.info}
          />
        </Box>
      )}

      <Divider />
    </Stack>
  );
};

export const RecommendedReviewers = ({
  reviewers,
  title,
  submissionContextId,
  submissionId,
  isOpen,
  onToggle
}: RecommendedReviewersProps) => {
  const { addReviewer, removeReviewersByEmails, reviewerList } =
    useReviewerInviteList();
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [apiInvitedEmails, setApiInvitedEmails] = useState<Set<string>>(
    new Set()
  );
  const [reviewerCOI, setReviewerCOI] = useState<Record<string, IReviewerCOI>>(
    {}
  );

  const reviewerEmails = useMemo(
    () =>
      reviewers
        .map(({ email }) => email)
        .filter((email): email is string => Boolean(email)),
    [reviewers]
  );

  useEffect(() => {
    if (!reviewerEmails?.length || !submissionContextId || !submissionId) {
      return;
    }

    validatorForAddReviewer(submissionContextId, submissionId, reviewerEmails)
      .then(results => {
        const invitedEmails = new Set<string>();
        const coiMap: Record<string, IReviewerCOI> = {};

        results?.forEach(result => {
          if (result.isAllowed === false) {
            invitedEmails.add(result.email);
          }
          coiMap[result.email] = result;
        });

        setApiInvitedEmails(invitedEmails);
        setReviewerCOI(coiMap);
      })
      .catch(() => {
        // Silently ignore validation errors
      });
  }, [reviewerEmails, submissionContextId, submissionId]);

  const handleCheckboxChange = useCallback(
    (reviewer: IReviewerRecommendation) => {
      const { email } = reviewer;

      setSelectedEmails(previous => {
        const next = new Set(previous);

        if (next.has(email)) {
          next.delete(email);
          removeReviewersByEmails([email]);
          return next;
        }
        next.add(email);
        addReviewer({
          email,
          source: 'manual',
          meta: {
            name: [reviewer?.firstName, reviewer?.lastName]
              .filter(Boolean)
              .join(' '),
            institute: reviewer?.affiliation
          }
        });

        return next;
      });
    },
    [addReviewer, removeReviewersByEmails]
  );

  const isReviewerInvited = useCallback(
    (email: string) =>
      reviewerList?.some(
        reviewer => reviewer?.email === email && reviewer?.invited
      ) || apiInvitedEmails?.has(email),
    [reviewerList, apiInvitedEmails]
  );

  const renderReviewers = () => {
    if (!reviewers?.length) {
      return (
        <>
          <Text as="span">No</Text>
          <Text
            as={'span'}
            textTransform={'lowercase'}
          >{` ${title} Are Available for This Submission`}</Text>
        </>
      );
    }

    return reviewers?.map((reviewer, index) => {
      const { email } = reviewer;

      return (
        <ReviewerItem
          key={email ?? index}
          reviewer={reviewer}
          index={index}
          isSelected={selectedEmails.has(email)}
          isInvited={isReviewerInvited(email)}
          coi={email ? reviewerCOI[email] : undefined}
          onChange={handleCheckboxChange}
        />
      );
    });
  };

  return (
    <Accordion
      chakraAccordionProps={{
        allowMultiple: true,
        ...(isOpen !== undefined
          ? {
              index: isOpen ? [0] : [],
              onChange: (idx: number[]) => onToggle?.(idx.includes(0))
            }
          : { defaultIndex: [] }),
        width: '100%',
        background: 'surfacePrimaryFill'
      }}
      items={[
        {
          accordionItemProps: {
            marginBottom: 0.5
          },
          title: (
            <Text textStyle="headingItem">
              {`${title} (${reviewers?.length})`}
            </Text>
          ),
          content: renderReviewers()
        }
      ]}
    />
  );
};
